<template>
  <!-- Notifications sidebar panel -->
  <div :class="{ hide: !isOpened }" class="main-notifs-panel">
    <div class="notifs-grid">
      <!-- Notifications panel header -->
      <div class="notifs-header">
        <div class="notifs-drawer" @click="closePanel">
          <arrow-right :size="28" />
        </div>
        <div class="notifs-title">{{ notifs.length }} Notifications</div>
        <div class="clear-btn-block">
          <button class="clear-btn" @click="confirmClearNotifications">
            Clear All
          </button>
        </div>
      </div>
      <!-- Notifications list -->
      <div
        v-loading="!initialized"
        class="notifs-container"
        element-loading-background="rgba(0, 0, 0, 0)"
        @scroll="readAllNotifications"
      >
        <message
          v-for="note in sortedNotifications"
          :key="note.id"
          :datetime="note.datetime"
          :title="note.title"
          :message="note.message"
          :is-read="note.isRead"
          :type="note.type"
          :category="note.category"
          :timezone="timezone"
          @removed="removeNotification(note.id)"
        />
      </div>
    </div>
  </div>
</template>

<script>
import Message from "./Message.vue";
import ArrowRight from "vue-material-design-icons/ArrowRight.vue";
import sharingSoundFile from "@/assets/sounds/sharing_sound.mp3";
import soundErrorFile from "@/assets/sounds/sound_error.mp3";
import { mapState, mapActions } from "vuex";

import {
  subscribeToNotificationUpdates,
  removeNotification,
  removeAllNotifications,
  readNotifications
} from "@/api/notifications.js";

// @group Notifications
// Notifications panel
export default {
  name: "Notifications",
  components: {
    Message,
    ArrowRight
  },
  props: {
    // Timezone name
    timezone: {
      type: String,
      default: "UTC"
    },
    // Is panel expanded/collapsed
    isOpened: {
      type: Boolean,
      required: true
    }
  },
  data() {
    return {
      notifs: [],
      initialized: false,
      sound_success_file: null,
      sound_error_file: null
    };
  },
  computed: {
    ...mapState({
      /** modals */
      modals: state => state.modals.clipper.MODALS,
      openedModal: state => state.modals.clipper.opened,
      modalData: state => state.modals.clipper.modalData
    }),
    // @vuese
    // Notifications sorted by time (from newer to older)
    sortedNotifications() {
      const sortedNotifs = [...this.notifs];
      sortedNotifs.sort((m1, m2) => m2.datetime - m1.datetime);
      return sortedNotifs;
    },
    // @vuese
    // Is unread message
    isNewNotif() {
      for (let i = 0; i < this.notifs.length; i++) {
        if (!this.notifs[i].isRead) {
          return true;
        }
      }
      return false;
    }
  },
  watch: {
    // @vuese
    // Set notifications read when user open panel
    isOpened(value) {
      if (value) {
        this.readAllNotifications();
      }
    },
    // @vuese
    // Signal to parent component that here are unread messages or not
    isNewNotif(isNew) {
      if (isNew) {
        if (this.sortedNotifications[0].category === "sharing") {

          this.openClipperModal({
            modal: this.modals.MESSAGEMODAL,
            data: this.sortedNotifications[0]
          });
        }
        this.$emit("notificationReceived");
      } else {
        this.$emit("notificationsRead");
      }
    },
    notifs() {
      if (this.isNewNotif) {
        if (this.sortedNotifications[0].category === "sharing") {
          this.openClipperModal({
            modal: this.modals.MESSAGEMODAL,
            data: this.sortedNotifications[0]
          });
        }
      }

    }
  },
  mounted() {
    subscribeToNotificationUpdates(
      (newNotif, firstNotif) =>
        this.handleNewNotification(newNotif, firstNotif),
      readId => this.handleReadNotification(readId),
      removeId => this.handleRemovedNotification(removeId),
      () => (this.initialized = true),
      error => this.showError("Notifications Requesting Error", error)
    );
  },
  methods: {
    ...mapActions("modals", ["openClipperModal", "closeClipperModal"]),
    // @vuese
    // Remove notification from database and from client data
    // @arg id - notification identifier
    removeNotification(id) {
      try {
        removeNotification(id);
      } catch (error) {
        this.showError("Remove Notification Failed", error);
      }
    },

    // @vuese
    // Show clear notifications confirmation form
    confirmClearNotifications() {
      this.$confirm(
        `Are you sure you want to delete all notifications?`,
        "Clear All",
        {
          confirmButtonText: "Clear",
          cancelButtonText: "Cancel"
        }
      ).then(() => {
        this.clearNotifications();
      });
    },
    // @vuese
    // Remove all notifications
    async clearNotifications() {
      try {
        await removeAllNotifications();
      } catch (error) {
        this.showError("Clear Notification Failed", error);
      }
    },
    // @vuese
    // Set all notifications 'read' status
    async readAllNotifications() {
      try {
        await readNotifications();
      } catch (error) {
        this.showError("Read Notifications Failed", error);
      }
    },
    // @vuese
    // Add notification to local list
    // @arg data - unparsed notofication data
    handleNewNotification(data, firstNotif) {
      const notifObj = this.getMessageObj(data);
      this.notifs = [...this.notifs, notifObj];
      if (firstNotif) {
        if (notifObj.type == "success") {
          this.sound_success_file = new Audio(sharingSoundFile);
          this.sound_success_file.play();
        }
        if (notifObj.type == "error") {
          this.sound_error_file = new Audio(soundErrorFile);
          this.sound_error_file.play();
        }
      }
      if (notifObj.isRead == false) {
        // if (notifObj.type == "success") {
        // this.sound_success_file = new Audio(sharingSoundFile);
        // this.sound_success_file.play();
        // }
        // if (notifObj.type == "error") {
        //   this.sound_error_file = new Audio(soundErrorFile);
        //   this.sound_error_file.play();
        // }
      }
    },
    // @vuese
    // Set notification read in local list
    // @arg id - read notification ID
    handleReadNotification(id) {
      const notif = this.notifs.find(n => n.id == id);
      notif.isRead = true;
    },
    // @vuese
    // Remove notification from local list
    // @arg id - removed notification ID
    handleRemovedNotification(id) {
      const removedIdx = this.notifs.findIndex(n => n.id == id);
      if (removedIdx >= 0) {
        this.notifs.splice(removedIdx, 1);
      }
    },
    // @vuese
    // Parse notification item to JS object
    // @arg data - notification data received from back-end response
    getMessageObj(data) {
      if (!data.isRead) {
        this.$emit("update:isNewNotif", true);
      }
      return {
        id: data.id,
        datetime: new Date(data.date),
        type: data.level,
        category: data.category,
        title: data.title,
        message: data.message,
        isRead: data.isRead,
        details: data.details
      };
    },
    // @vuese
    // Expand panel
    openPanel() {
      this.$emit("opened");
    },
    // @vuese
    // Collapse panel
    closePanel() {
      this.$emit("closed");
    },
    // @vuese
    // Show error message
    // @arg title - Error title <br>
    // @arg error - Error description
    showError(title, error) {
      this.$notify({
        group: "foo",
        position: "left top",
        type: "error",
        title: title,
        text: error
      });
    }
  }
};
</script>

<style lang="scss">
.main-notifs-panel {
  position: fixed;
  width: 30%;
  right: 0;
  top: 0;
  bottom: 0;

  box-shadow: 0 0 12px rgba(0, 0, 0, 0.5);

  padding-top: 10px;

  background-color: #333;
  color: white;

  transition: right 0.3s;
}
.main-notifs-panel.hide {
  right: -30%;
}

.notifs-grid {
  height: 100%;

  display: grid;
  grid-template-rows: 50px auto;
  grid-template-areas:
    "header"
    "notifs";

  padding: 0 2vw 0 2vw;
}

.notifs-header {
  grid-area: header;

  display: flex;
  justify-content: space-between;
  align-items: center;

  background-color: #333;
}
.notifs-drawer {
  font-size: 30px;
  cursor: pointer;
  color: #ddd;
}
.notifs-drawer:hover {
  color: white;
}
.notifs-title {
  // font-family: Verdana, sans-serif;
  font-size: 1.25rem;
  text-align: center;
}
.clear-btn-block {
  margin-right: 10px;
}
.clear-btn {
  // font-family: Verdana, sans-serif;
  font-size: 1rem;

  height: 35px;
  padding-left: 15px;
  padding-right: 15px;
  border: none;
  border-radius: 5px;
  background-color: #ed5229;
  color: white;
  cursor: pointer;
}
.clear-btn:focus,
.clear-btn:hover {
  background-color: #ed5229;
}

.notifs-container {
  grid-area: notifs;
  overflow-y: auto;

  background-color: #333;

  padding-right: 10px;
}

/* Sharing modal scrollbar */
/* width */
.notifs-grid ::-webkit-scrollbar {
  width: 15px;
}

/* Track */
.notifs-grid ::-webkit-scrollbar-track {
  background: #777;
  border-radius: 10px;
}

/* Handle */
.notifs-grid ::-webkit-scrollbar-thumb {
  background: #555;
  border-radius: 10px;
}

/* Handle on hover */
.notifs-grid ::-webkit-scrollbar-thumb:hover {
  background: #444;
  border-radius: 10px;
}

@media only screen and (max-width: 1200px) {
  .main-notifs-panel {
    width: 40%;
  }
  .main-notifs-panel.hide {
    right: -40%;
  }
}

@media only screen and (max-width: 992px) {
  .main-notifs-panel {
    width: 100%;
  }
  .main-notifs-panel.hide {
    right: -100%;
  }
  .notifs-grid {
    display: grid;
    grid-template-rows: 80px auto;
    grid-template-areas:
      "header"
      "notifs";
  }
  .notifs-header {
    margin-top: 30px;
    flex-direction: row;
    flex-wrap: wrap;
    justify-content: center;
  }
  .notifs-drawer {
    position: absolute;
    top: 10px;
    left: 10px;
  }
  .notifs-title {
    margin-right: 10px;

    white-space: nowrap;
    text-overflow: ellipsis;
  }
}
</style>
